<template id="game-configuration-form">
  <form class="game-form">
    <div class="columns">
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">Game name:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <b-field :type="getFieldTypeByInput('gameNameInput')"
                  :message="getErrorMessageByInput('gameNameInput')">
                  <b-input v-model="game.gameName"
                    v-validate="{required:true}"
                    name="gameNameInput"
                    placeholder="A simple name to define your game"></b-input>
                </b-field>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label" style="width: 180px">Game description:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <b-field :type="getFieldTypeByInput('gameDescriptionInput')"
                  :message="getErrorMessageByInput('gameDescriptionInput')">
                  <b-input v-model="game.gameDescription"
                    v-validate="{required:true}"
                    name="gameDescriptionInput"
                    placeholder="A longer description"></b-input>
                </b-field>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label" style="width: 180px"></label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <b-message type="is-warning">
                  If Autostop is enabled, the game will stop automatically 4 hours from start time
                </b-message>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label" style="width: 180px">Autostop enabled:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <b-switch
                  v-model="game.autoStop"
                  :value="game.autoStop"
                ></b-switch>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label" style="width: 180px"></label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <b-message type="is-warning">
                  The 2 following fields will be displayed in the Teams' screens. Leave empty for default values
                </b-message>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">Game title:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <input
                  class="input"
                  v-model="game.gameDisplayedName"
                  type="text"
                  placeholder="If you leave this text empty, the text saved in your general settings will be displayed">
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">Game logo:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <div style="position: relative">
                  <picture-input
                    id="pictureInput"
                    ref="gameLogo"
                    @change="onPictureSelected"
                    width="250"
                    height="150"
                    buttonClass="button is-primary is-outlined"
                    removeButtonClass="button is-outlined"
                    :customStrings="fileUploadCustomTexts"
                    :removable="true"
                  />
                  <div v-if="logoPreviewUrl" id="injectedPreview" style="background-color: white">
                    <img id="injectedPreviewImage" v-bind:src="logoPreviewUrl"/>
                  </div>
                  <button v-if="logoPreviewUrl" @click.prevent="hideImagePreview" class="button is-info is-outlined"
                    style="width: 250px; margin-top: 5px;">Change logo
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label" style="width: 180px">Accuracy:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <b-select placeholder="Select a length" v-model="game.questionRadius">
                <option
                  v-for="radius in radiusMap"
                  :value="radius.value"
                  :selected="game.questionRadius === radius.value"
                  :key="radius.id"
                >
                  {{ radius.text }}
                </option>
              </b-select>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">Start date:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <b-field :type="getFieldTypeByInput('gameStartDate')"
                  :message="getErrorMessageByInput('gameStartDate')">
                  <b-datepicker
                    v-validate="{required:true}"
                    name="gameStartDate"
                    v-model="game.startDate"
                    placeholder="Select start date"
                    icon="calendar-today"
                    :focused-date="new Date()"
                  ></b-datepicker>
                </b-field>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">Start time:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <b-field :type="getFieldTypeByInput('gameStartTime')"
                  :message="getErrorMessageByInput('gameStartTime')">
                  <b-clockpicker
                    name="gameStartTime"
                    v-validate="{required:true}"
                    v-model="game.startTime"
                    :increment-minutes="15"
                    rounded
                    placeholder="Select start time"
                    icon="clock"

                    :hour-format="format">
                  </b-clockpicker>
                </b-field>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">End date:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <b-field :type="getFieldTypeByInput('gameEndDate')" :message="getErrorMessageByInput('gameEndDate')">
                  <b-datepicker
                    name="gameEndDate"
                    v-validate="{required:true}"
                    v-model="game.endDate"
                    placeholder="Select end date"
                    icon="calendar-today"
                    :focused-date="new Date()"
                  ></b-datepicker>
                </b-field>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label">End time:</label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <b-field :type="getFieldTypeByInput('gameEndTime')" :message="getErrorMessageByInput('gameEndTime')">
                  <b-clockpicker
                    v-validate="{required:true}"
                    name="gameEndTime"
                    v-model="game.endTime"
                    :increment-minutes="15"
                    rounded
                    placeholder="Select end time"
                    icon="clock"
                    :hour-format="format">
                  </b-clockpicker>
                </b-field>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column">
        <div class="field is-horizontal">
          <div class="field-label is-normal">
            <label class="label"></label>
          </div>
          <div class="field-body">
            <div class="field">
              <div class="control">
                <b-message type="is-success">
                  The below fields will center the map players will see when they open the app. The zoom factor is
                  useful when geolocation points are too close to each other.
                </b-message>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <geo-selector
      ref="geo_selector"
      class="geo-selector"
      :disable-auto-locate="disableAutoLocate"
      :showZoom=true
      @updated="onGeoSelectorUpdated"/>
    <b-loading :is-full-page="true" :active.sync="isLoading"></b-loading>
  </form>
</template>
<script>
  /* eslint-disable indent */

  import GeoSelector from './GeoSelector.vue'
  import PictureInput from 'vue-picture-input'
  import BSwitch from '../../../node_modules/buefy/src/components/switch/Switch.vue'
  import imageHelper from '../../helpers/image-helper'

  export default {
    name: 'GameConfigurationForm',
    components: {
      BSwitch,
      GeoSelector,
      PictureInput,
    },
    props: {
      disableAutoLocate: {
        type: Boolean,
        required: false,
      },
      isLoading: {
        type: Boolean,
        required: true,
      },
    },
    computed: {
      format () {
        return '12'
      }
    },
    data () {
      return {
        fileUploadCustomTexts: {
          upload: '<h1>Bummer!</h1>',
          drag: 'Drop file here or click here to upload'
        },
        logoPreviewUrl: '',
        game: {
          gameDisplayedName: '',
          gameLogo: null,
          gameName: '',
          autoStop: false,
          gameDescription: '',
          questionRadius: 80,
          geo: {
            lat: 0,
            lng: 0,
            zoom: 0
          },
          startTime: null,
          startDate: null,
          endTime: null,
          endDate: null,
        },
        radiusMap: [{
          id: 'small',
          text: 'High (suitable for confined areas): 30m',
          value: 30
        },
          {
            id: 'large',
            text: 'Low (suitable for vast areas): 80m',
            value: 80
          }],

      }
    },
    watch: {
      game: {
        handler: function () {
          const emitted = this.game
          this.$emit('updated', emitted)
        },
        deep: true,
      },
    },
    created () {
      this.$parent.$on('tryToValidateGame', this.validateFields)
    },
    methods: {
      getFieldTypeByInput (input) {
        return this.errors.has(input) ? 'is-danger' : ''
      },
      getErrorMessageByInput (input) {
        return this.errors.has(input) ? this.errors.first(input) : ''
      },
      validateFields () {
        this.$validator.validateAll()
          .then((valid) => {
            this.$emit('gameValidated', valid)
          })
      },
      hideImagePreview () {
        this.logoPreviewUrl = ''
      },
      injectLogoPreview (logoUrl) {
        this.logoPreviewUrl = logoUrl
      },
      setGeo (geo) {
        this.$refs.geo_selector.setGeo(geo)
      },
      onPictureSelected(gameLogo) {
        if (gameLogo) {
          imageHelper.compressImg(this.$refs.gameLogo.image)
          .then((compressedImgDataUrl) => {
            this.game.gameLogo = compressedImgDataUrl
          })
        }
      },
      onGeoSelectorUpdated (geo) {
        this.game.geo = geo
      },
      preFillFields (game) {
        this.game = game
      },
    }
  }
</script>

<style scoped>
  .game-form .is-horizontal .field-label {
    flex-grow: 0;
  }

  .game-form label {
    margin-bottom: 0;
    width: 180px !important;
  }

  .game-form .column .field {
    align-items: center;
  }

  .date-time /deep/ {
    z-index: 15 !important;
  }

  .picture-input /deep/ .preview-container {
    margin: 0;
    z-index: 1 !important;
  }

  .picture-input /deep/ .picture-preview {
    margin: 0;
    z-index: 1 !important;
  }

  .picture-input /deep/ .picture-inner {
    z-index: 1 !important;
  }

  .picture-input {
    text-align: left !important;
  }

  .picture-input /deep/ .preview-container {
    margin: 0;
  }

  #pictureInput, #injectedPreview {
    height: 150px;
    width: 250px;
    position: absolute;
  }

  #injectedPreviewImage {
    height: 150px;
    width: 250px;
  }

  #injectedPreview {
    top: 0;
    z-index: 10;
  }
</style>
